import { ConsentTrackingStatus } from "@tandemdiabetes/sugarmate-api-react-client"
import { ActionButton } from "@tandemdiabetes/sugarmate-react-components"
import { useNavigate } from "@tanstack/react-router"
import type { MouseEventHandler } from "react"
import {
  useCallback,
  useMemo,
  useState,
} from "react"
import { DataShareConsentDialog } from "./DataShareConsentDialog"
import { FollowerInvitesList } from "./FollowerInvitesList"
import { FollowersList } from "./FollowersList"
import { NoDataSourceDialog } from "./NoDataSourceDialog"
import styles from "./Sharing.module.scss"
import { PageTemplate } from "src/components"
import { usePolyglot } from "src/contexts"
import {
  useConsents,
  useFollowInvites,
  useSharingFollows,
  useSources,
} from "src/hooks"

const polyglotPrefix = "pages.data_sharing.share_my_data."

export function Sharing() {
  const polyglot = usePolyglot()
  const navigate = useNavigate()
  const followers = useSharingFollows()
  const { data: followInvites } = useFollowInvites()
  const { data: consents } = useConsents()
  const limitExceeded = useMemo(
    () => followers.length + (followInvites?.length ?? 0) >= 20,
    [
      followers,
      followInvites,
    ],
  )

  const { data: sources } = useSources()

  const noActiveFollowers = useMemo(
    () => followers.length + (followInvites?.length ?? 0) === 0,
    [
      followers,
      followInvites,
    ],
  )

  const [
    noDataSourceDialogOpen,
    setNoDataSourceDialogOpen,
  ] =
    useState<boolean>(false)
  const [
    dataShareConsentDialogOpen,
    setDataShareConsentDialogOpen,
  ] =
    useState<boolean>(false)

  const onPress = useCallback(
    () => {
      if (!sources || sources.length === 0) {
        setNoDataSourceDialogOpen(true)
        return
      }

      const shareConsent =
        consents?.share_consent.status ?? ConsentTrackingStatus.Unacknowledged
      if (
        shareConsent === ConsentTrackingStatus.Unacknowledged ||
      shareConsent === ConsentTrackingStatus.Declined
      ) {
        setDataShareConsentDialogOpen(true)
        return
      }
      void navigate({ to: "/data_sharing/sharing/invite" })
    },
    [
      navigate,
      consents?.share_consent.status,
      sources,
      setNoDataSourceDialogOpen,
      setDataShareConsentDialogOpen,
    ],
  )
  const navigateToDataSource: MouseEventHandler<HTMLButtonElement> = useCallback(
    () => {
      void (() => {
        void navigate({ to: "/account/data_source" })
        setNoDataSourceDialogOpen(false)
      })()
    },
    [
      navigate,
      setNoDataSourceDialogOpen,
    ],
  )

  return (
    <PageTemplate
      user="account"
      title={polyglot.t("components.page_header.pages.sharing")}
      backRoutePath="/data_sharing"
    >
      <div className={styles.container}>
        <div className={styles.actionContainer}>
          <div className={styles.noUsersText}>
            {noActiveFollowers && (
              <div>
                {polyglot.t(`${polyglotPrefix}no_active_followers`)}
              </div>
            )}
          </div>
          <div className={styles.description}>
            {polyglot.t(
              limitExceeded
                ? `${polyglotPrefix}description_limit_exceeded`
                : `${polyglotPrefix}description`,
            )}
          </div>
          <ActionButton
            id="invite-follower-btn"
            className={styles.button}
            stylePreset="primary"
            onClick={onPress}
            disabled={limitExceeded}
          >
            {polyglot.t(`${polyglotPrefix}invite_button.label`)}
          </ActionButton>
        </div>
        {(followInvites?.length ?? 0) > 0 && <FollowerInvitesList/>}
        {followers.length > 0 && <FollowersList/>}
        <NoDataSourceDialog
          open={noDataSourceDialogOpen}
          setOpen={setNoDataSourceDialogOpen}
          navigateToDataSource={navigateToDataSource}
        />
        <DataShareConsentDialog
          open={dataShareConsentDialogOpen}
          setOpen={setDataShareConsentDialogOpen}
        />
      </div>
    </PageTemplate>
  )
}
